import { Action, combineReducers } from "redux";
import { createReducer, IAction } from "reduxsauce";

import { Types as AllTypes } from "../../../actions";
import { ICourse, IScormPackage } from "../../types/common";
import { FetchPartInstanceAnalytics, IUnitsAnalytics, IUser, IUsersAnalytics } from "../Common";

export interface IState {
  _: IScormPackageAnalyticsState;
}

export interface IScormPackageAnalyticsState {
  course: ICourse | null;
  scormPackage: IScormPackage | null;
  fetchScormPackageDataRequestState: FetchScormPackageDataRequestState;
  fetchScormPackageDataRequestError: string | null;
  fetchCourseDataRequestState: FetchCourseDataRequestState;
  fetchCourseDataRequestError: string | null;
  unitsAnalytics: IUnitsAnalytics[] | null;
  users: IUser[] | null;
  usersAnalytics: IUsersAnalytics | null;
  fetchAllUsersRequestState: FetchAllUsersRequestState;
  fetchAllUsersRequestError: string | null;
  fetchUsersAnalyticsRequestState: FetchUsersAnalyticsRequestState;
  fetchUsersAnalyticsRequestError: string | null;
  fetchScormPackagePartInstSummaryRollup: FetchPartInstanceAnalytics;
}

export enum FetchAllUsersRequestState {
  IDLE = "IDLE",
  PENDING = "PENDING",
  FAILED = "FAILED",
  SUCCEEDED = "SUCCEEDED",
}

export enum FetchUsersAnalyticsRequestState {
  IDLE = "IDLE",
  PENDING = "PENDING",
  FAILED = "FAILED",
  SUCCEEDED = "SUCCEEDED",
}

export enum FetchScormPackageDataRequestState {
  IDLE = "IDLE",
  PENDING = "PENDING",
  FAILED = "FAILED",
  SUCCEEDED = "SUCCEEDED",
}

export enum FetchCourseDataRequestState {
  IDLE = "IDLE",
  PENDING = "PENDING",
  FAILED = "FAILED",
  SUCCEEDED = "SUCCEEDED",
}

const Types = (AllTypes as any)._APP._SCORM_PACKAGE_ANALYTICS;

export const INITIAL_STATE: IScormPackageAnalyticsState = {
  course: null,
  fetchAllUsersRequestError: null,
  fetchAllUsersRequestState: FetchAllUsersRequestState.IDLE,
  fetchCourseDataRequestError: null,
  fetchCourseDataRequestState: FetchCourseDataRequestState.IDLE,
  fetchScormPackageDataRequestError: null,
  fetchScormPackageDataRequestState: FetchScormPackageDataRequestState.IDLE,
  fetchScormPackagePartInstSummaryRollup: null,
  fetchUsersAnalyticsRequestError: null,
  fetchUsersAnalyticsRequestState: FetchUsersAnalyticsRequestState.IDLE,
  scormPackage: null,
  unitsAnalytics: null,
  users: null,
  usersAnalytics: null,
};

export function mount(state: IScormPackageAnalyticsState, action: IAction): IScormPackageAnalyticsState {
  return INITIAL_STATE;
}

export function unmount(state: IScormPackageAnalyticsState, action: IAction): IScormPackageAnalyticsState {
  return INITIAL_STATE;
}

export function fetchScormPackageData(
  state: IScormPackageAnalyticsState,
  action: IAction
): IScormPackageAnalyticsState {
  return {
    ...state,
    fetchScormPackageDataRequestError: null,
    fetchScormPackageDataRequestState: FetchScormPackageDataRequestState.PENDING,
  };
}

export function handleFetchScormPackageDataFailure(
  state: IScormPackageAnalyticsState,
  action: IAction
): IScormPackageAnalyticsState {
  return {
    ...state,
    fetchScormPackageDataRequestError: action.error.message,
    fetchScormPackageDataRequestState: FetchScormPackageDataRequestState.FAILED,
  };
}

export function handleFetchScormPackageDataSuccess(
  state: IScormPackageAnalyticsState,
  action: IAction
): IScormPackageAnalyticsState {
  return {
    ...state,
    fetchScormPackageDataRequestError: null,
    fetchScormPackageDataRequestState: FetchScormPackageDataRequestState.SUCCEEDED,
    scormPackage: action.scormPackage,
  };
}

export function fetchCourseData(state: IScormPackageAnalyticsState, action: IAction): IScormPackageAnalyticsState {
  return {
    ...state,
    fetchCourseDataRequestError: null,
    fetchCourseDataRequestState: FetchCourseDataRequestState.PENDING,
  };
}

export function handleFetchCourseDataFailure(
  state: IScormPackageAnalyticsState,
  action: IAction
): IScormPackageAnalyticsState {
  return {
    ...state,
    fetchCourseDataRequestError: action.error.message,
    fetchCourseDataRequestState: FetchCourseDataRequestState.FAILED,
  };
}

export function handleFetchCourseDataSuccess(
  state: IScormPackageAnalyticsState,
  action: IAction
): IScormPackageAnalyticsState {
  return {
    ...state,
    course: action.course,
    fetchCourseDataRequestError: null,
    fetchCourseDataRequestState: FetchCourseDataRequestState.SUCCEEDED,
  };
}

export function fetchAllUsers(state: IScormPackageAnalyticsState, action: IAction): IScormPackageAnalyticsState {
  return {
    ...state,
    fetchAllUsersRequestError: null,
    fetchAllUsersRequestState: FetchAllUsersRequestState.PENDING,
  };
}

export function handleFetchAllUsersFailure(
  state: IScormPackageAnalyticsState,
  action: IAction
): IScormPackageAnalyticsState {
  return {
    ...state,
    fetchAllUsersRequestError: action.error.message,
    fetchAllUsersRequestState: FetchAllUsersRequestState.FAILED,
  };
}

export function handleFetchAllUsersSuccess(
  state: IScormPackageAnalyticsState,
  action: IAction
): IScormPackageAnalyticsState {
  return {
    ...state,
    fetchAllUsersRequestError: null,
    fetchAllUsersRequestState: FetchAllUsersRequestState.SUCCEEDED,
    users: action.users,
  };
}

export function fetchUsersAnalytics(state: IScormPackageAnalyticsState, action: IAction): IScormPackageAnalyticsState {
  return {
    ...state,
    fetchUsersAnalyticsRequestError: null,
    fetchUsersAnalyticsRequestState: FetchUsersAnalyticsRequestState.PENDING,
  };
}

export function handleFetchUsersAnalyticsFailure(
  state: IScormPackageAnalyticsState,
  action: IAction
): IScormPackageAnalyticsState {
  return {
    ...state,
    fetchUsersAnalyticsRequestError: action.error.message,
    fetchUsersAnalyticsRequestState: FetchUsersAnalyticsRequestState.FAILED,
  };
}

export function handleFetchUsersAnalyticsSuccess(
  state: IScormPackageAnalyticsState,
  action: IAction
): IScormPackageAnalyticsState {
  return {
    ...state,
    fetchScormPackagePartInstSummaryRollup: action.fetchScormPackagePartInstSummaryRollup,
    fetchUsersAnalyticsRequestError: null,
    fetchUsersAnalyticsRequestState: FetchUsersAnalyticsRequestState.SUCCEEDED,
    unitsAnalytics: action.unitsAnalytics,
    usersAnalytics: action.usersAnalytics,
  };
}

export default combineReducers<IState, Action>({
  _: createReducer<IScormPackageAnalyticsState>(INITIAL_STATE, {
    [Types.MOUNT]: mount,
    [Types.FETCH_SCORM_PACKAGE_DATA]: fetchScormPackageData,
    [Types.HANDLE_FETCH_SCORM_PACKAGE_DATA_FAILURE]: handleFetchScormPackageDataFailure,
    [Types.HANDLE_FETCH_SCORM_PACKAGE_DATA_SUCCESS]: handleFetchScormPackageDataSuccess,
    [Types.FETCH_COURSE_DATA]: fetchCourseData,
    [Types.HANDLE_FETCH_COURSE_DATA_FAILURE]: handleFetchCourseDataFailure,
    [Types.HANDLE_FETCH_COURSE_DATA_SUCCESS]: handleFetchCourseDataSuccess,
    [Types.FETCH_ALL_USERS]: fetchAllUsers,
    [Types.HANDLE_FETCH_ALL_USERS_FAILURE]: handleFetchAllUsersFailure,
    [Types.HANDLE_FETCH_ALL_USERS_SUCCESS]: handleFetchAllUsersSuccess,
    [Types.FETCH_USERS_ANALYTICS]: fetchUsersAnalytics,
    [Types.HANDLE_FETCH_USERS_ANALYTICS_FAILURE]: handleFetchUsersAnalyticsFailure,
    [Types.HANDLE_FETCH_USERS_ANALYTICS_SUCCESS]: handleFetchUsersAnalyticsSuccess,
    [Types.UNMOUNT]: unmount,
  }),
});
